import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BellRing } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardSummary } from '../../../../../packages/contracts/src/index.js';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Select } from '../../components/ui/field';
import { ListDateFilters } from '../../components/ui/list-date-filters';
import { PaginationControls, useControlledPagination } from '../../components/ui/pagination';
import { SearchBox } from '../../components/ui/search';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { api } from '../../shared/api';
import { formatDate, formatDateTime } from '../../shared/format';
import { dateRangeForPreset, isWithinDateRange, useUrlListFilters } from '../../shared/listFilters';
import { matchesSearch } from '../../shared/search';

type Alert = DashboardSummary['alerts'][number];

const alertTypeLabels: Record<Alert['type'], string> = {
  grooming: 'Grooming',
  spa: 'Spa',
  transportation: 'Transporte',
  'stay-check-in': 'Entrada hotel',
  'stay-check-out': 'Salida hotel',
  'daycare-check-in': 'Entrada guarderia',
  'daycare-check-out': 'Salida guarderia',
  other: 'Otro',
};

const alertLevelLabels: Record<Alert['level'], string> = {
  info: 'Info',
  warning: 'Alerta',
  critical: 'Critico',
};

export function DashboardAlertsPage() {
  const defaultRange = useMemo(() => dateRangeForPreset('today'), []);
  const defaults = useMemo(() => ({ q: '', range: 'today', from: defaultRange.from, to: defaultRange.to, type: '', level: '', page: '1', pageSize: '10' }), [defaultRange]);
  const { values: filters, setValue, setValues } = useUrlListFilters(defaults, { keepDefaults: true });
  const page = Math.max(1, Number(filters.page) || 1);
  const pageSize = Math.max(10, Number(filters.pageSize) || 10);
  const summaryQuery = useQuery({
    queryKey: ['dashboard-summary', 'alerts-list', filters.range, filters.from, filters.to],
    queryFn: () => api.dashboard({ period: filters.range, from: filters.from, to: filters.to }),
  });
  const alerts = summaryQuery.data?.alerts ?? [];
  const filteredAlerts = useMemo(() => alerts.filter((alert) => {
    if (filters.type && alert.type !== filters.type) return false;
    if (filters.level && alert.level !== filters.level) return false;
    return isWithinDateRange(alert.scheduledAt, filters.from, filters.to) && matchesSearch(filters.q, [alert.message, alert.type, alertTypeLabels[alert.type], alert.level, alertLevelLabels[alert.level], alert.scheduledAt]);
  }), [alerts, filters.from, filters.level, filters.q, filters.to, filters.type]);
  const alertsPage = useControlledPagination(filteredAlerts, page, (nextPage) => setValue('page', String(nextPage)), pageSize);

  if (summaryQuery.isLoading) return <LoadingState label="Cargando alertas" />;
  if (summaryQuery.isError) return <ErrorState message={summaryQuery.error.message} />;

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-start gap-3">
        <Link className="inline-flex h-9 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to="/admin/dashboard">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Dashboard
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">Alertas</h1>
          <p className="mt-1 text-sm text-slate-500">Busca eventos programados por tipo, nivel, perro o fecha.</p>
        </div>
      </div>
      <Card>
        <CardHeader>
          <div className="grid gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <SearchBox value={filters.q} onChange={(value) => setValue('q', value)} placeholder="Evento, tipo o fecha" label="Buscar alertas" />
              <Select value={filters.type} onChange={(event) => setValue('type', event.target.value)} aria-label="Filtrar por tipo">
                <option value="">Todos los tipos</option>
                {Object.entries(alertTypeLabels).map(([type, label]) => <option key={type} value={type}>{label}</option>)}
              </Select>
              <Select value={filters.level} onChange={(event) => setValue('level', event.target.value)} aria-label="Filtrar por nivel">
                <option value="">Todos los niveles</option>
                {Object.entries(alertLevelLabels).map(([level, label]) => <option key={level} value={level}>{label}</option>)}
              </Select>
            </div>
            <ListDateFilters range={filters.range} from={filters.from} to={filters.to} onChange={setValues} startId="alertsFrom" endId="alertsTo" />
          </div>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? <EmptyState title="Sin alertas" body="No hay alertas programadas en este rango." /> : null}
          {alerts.length > 0 && filteredAlerts.length === 0 ? <EmptyState title="Sin resultados" body="Intenta con otro filtro, búsqueda o rango de fechas." /> : null}
          {filteredAlerts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <thead><tr><Th>Fecha</Th><Th>Tipo</Th><Th>Nivel</Th><Th>Alerta</Th></tr></thead>
                <tbody>
                  {alertsPage.pageItems.map((alert) => (
                    <tr key={alert.id}>
                      <Td>{formatAlertSchedule(alert)}</Td>
                      <Td><Badge>{alertTypeLabels[alert.type]}</Badge></Td>
                      <Td><Badge>{alertLevelLabels[alert.level]}</Badge></Td>
                      <Td className="font-medium text-[#223126]"><span className="inline-flex items-center gap-2"><BellRing className="h-4 w-4 text-[#cf7766]" aria-hidden="true" />{alert.message}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}
          <PaginationControls {...alertsPage} onPageChange={alertsPage.setPage} onPageSizeChange={(nextPageSize) => setValues({ pageSize: String(nextPageSize), page: '1' })} />
        </CardContent>
      </Card>
    </div>
  );
}

function formatAlertSchedule(alert: Alert) {
  return alert.type === 'stay-check-in' || alert.type === 'stay-check-out' ? formatDate(alert.scheduledAt) : formatDateTime(alert.scheduledAt);
}
